// Supabase auth seam.
//
// When VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY are set, this uses the
// Supabase JS client for a real email/password login (the backend then runs
// APP_AUTH_MODE=supabase and verifies the JWT). When they're absent, the app
// runs in local no-auth mode (a stub session; backend APP_AUTH_MODE=none),
// so local dev keeps working without a login.

import { type SupabaseClient, createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const authEnabled = !!(url && anonKey);
export const supabase: SupabaseClient | null = authEnabled
  ? createClient(url as string, anonKey as string)
  : null;

export interface Session {
  userId: string;
  email: string | null;
  accessToken: string | null;
}

const LOCAL_SESSION: Session = {
  userId: "local-dev",
  email: "local@dev",
  accessToken: null,
};

// Returns the current session, or null when auth is enabled but nobody is
// logged in (→ App shows the login screen). In local no-auth mode it always
// returns the stub session.
export async function getSession(): Promise<Session | null> {
  if (!supabase) return LOCAL_SESSION;
  const { data } = await supabase.auth.getSession();
  const s = data.session;
  if (!s) return null;
  return { userId: s.user.id, email: s.user.email ?? null, accessToken: s.access_token };
}

export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

// Logins are username-based for the client. Supabase auth is email-based, so a
// bare username is mapped to a fixed internal domain (the accounts are created
// as "<username>@anl.app"). An entry that already contains "@" is used as-is.
const USERNAME_DOMAIN = "anl.app";

export async function signIn(username: string, password: string): Promise<void> {
  if (!supabase) throw new Error("Auth is not configured");
  const email = username.includes("@") ? username : `${username}@${USERNAME_DOMAIN}`;
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  if (supabase) await supabase.auth.signOut();
}

// Subscribe to login/logout; returns an unsubscribe fn.
export function onAuthChange(cb: () => void): () => void {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange(() => cb());
  return () => data.subscription.unsubscribe();
}
